import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FEATURES } from '../../utils/constants';
import { fadeUp, spring, stagger, viewportOnce } from '../../utils/animations';

function FeatureCard({ feature }: { feature: typeof FEATURES[0] }) {
  return (
    <motion.div
      className="glass-card p-8 group transition-colors duration-300 hover:border-white/[0.15]"
      variants={fadeUp}
      transition={spring}
      whileHover={{
        y: -4,
        transition: { type: 'spring', stiffness: 400, damping: 25 },
      }}
    >
      {/* Icon */}
      <div className="text-2xl mb-5 w-12 h-12 rounded-xl bg-gradient-brand flex items-center justify-center text-white font-bold gpu transition-transform duration-300 group-hover:scale-105">
        {feature.icon}
      </div>

      {/* Title */}
      <h3 className="text-lg font-bold text-white mb-3">{feature.title}</h3>

      {/* Description */}
      <p className="text-white/45 leading-relaxed text-sm">{feature.description}</p>
    </motion.div>
  );
}

export default function Features() {
  const [ref, inView] = useInView(viewportOnce);

  return (
    <section className="section-padding relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial-blue opacity-10 gpu" />

      <div className="container-custom relative z-10" ref={ref}>
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          transition={spring}
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            See What Others Can't Hear
          </h2>
          <p className="text-lg text-white/40 max-w-xl mx-auto">
            AI hears what people won't tell you.
          </p>
        </motion.div>

        {/* Feature grid — staggered */}
        <motion.div
          className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto"
          variants={stagger(0.1, 0.2)}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {FEATURES.map((feature) => (
            <FeatureCard key={feature.id} feature={feature} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
